/**
 * User Analytics Tab
 * Comprehensive user analytics dashboard for tracking subscriptions, token usage,
 * conversion rates, and engagement metrics
 */

import { useUserAnalytics } from '@hooks/useUserAnalytics';
import { DollarSign, RefreshCw, TrendingUp, Users } from 'lucide-react';
import type { ReactElement } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const COLORS = {
  free: '#9ca3af',
  pro: '#3b82f6',
  premium: '#f59e0b',
  elite: '#6366f1',
  haiku: '#3b82f6',
  sonnet: '#8b5cf6',
  opus: '#ec4899',
  signups: '#10b981',
  conversions: '#f59e0b',
};

export function UserAnalyticsTab(): ReactElement {
  const { analytics, loading, error, refetch } = useUserAnalytics();

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-pulse space-y-4 w-full max-w-4xl">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-32 bg-theme-bg rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="text-center py-12">
        <p className="text-theme-danger mb-4">{error || 'Failed to load analytics'}</p>
        <button
          type="button"
          onClick={refetch}
          className="px-4 py-2 bg-theme-primary-fill text-theme-on-primary-fill rounded-lg hover:bg-theme-primary-fill/90"
        >
          Retry
        </button>
      </div>
    );
  }

  const { subscriptionBreakdown, tokenUsage, conversionMetrics, userGrowth, engagementMetrics } =
    analytics;

  return (
    <div className="space-y-8 pb-12">
      {/* Header */}
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-theme-text text-center">User Analytics</h2>
        <div className="flex justify-center">
          <button
            type="button"
            onClick={refetch}
            className="px-4 py-2 bg-theme-bg text-theme-text rounded-lg hover:bg-theme-bg-secondary transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh Data
          </button>
        </div>
      </div>

      {/* KPI Cards Row 1: Subscriptions & Revenue */}
      <div className="grid grid-cols-1 @3@7xl:grid-cols-2 @5@7xl:grid-cols-3 @7xl:grid-cols-6 gap-4">
        <div className="bg-theme-bg rounded-lg p-6 border border-theme-border">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-theme-text-secondary">Total Users</p>
            <Users className="w-5 h-5 text-theme-primary" />
          </div>
          <p className="text-3xl font-bold text-theme-text">{subscriptionBreakdown.total}</p>
        </div>

        <div className="bg-theme-bg rounded-lg p-6 border border-theme-border">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-theme-text-secondary">Pro Users</p>
            <TrendingUp className="w-5 h-5" style={{ color: COLORS.pro }} />
          </div>
          <p className="text-3xl font-bold text-theme-text">{subscriptionBreakdown.pro}</p>
          <p className="text-xs text-theme-text-tertiary mt-1">
            {subscriptionBreakdown.proPercentage}% of total
          </p>
        </div>

        <div className="bg-theme-bg rounded-lg p-6 border border-theme-border">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-theme-text-secondary">Premium Users</p>
            <TrendingUp className="w-5 h-5 text-theme-warning" />
          </div>
          <p className="text-3xl font-bold text-theme-text">{subscriptionBreakdown.premium}</p>
          <p className="text-xs text-theme-text-tertiary mt-1">
            {subscriptionBreakdown.premiumPercentage}% of total
          </p>
        </div>

        <div className="bg-theme-bg rounded-lg p-6 border border-theme-border">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-theme-text-secondary">Elite Users</p>
            <TrendingUp className="w-5 h-5" style={{ color: COLORS.elite }} />
          </div>
          <p className="text-3xl font-bold text-theme-text">{subscriptionBreakdown.elite}</p>
          <p className="text-xs text-theme-text-tertiary mt-1">
            {subscriptionBreakdown.elitePercentage}% of total
          </p>
        </div>

        <div className="bg-theme-bg rounded-lg p-6 border border-theme-border">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-theme-text-secondary">Monthly Revenue</p>
            <DollarSign className="w-5 h-5 text-theme-success" />
          </div>
          <p className="text-3xl font-bold text-theme-text">
            ${subscriptionBreakdown.revenue.monthly.toFixed(2)}
          </p>
          <p className="text-xs text-theme-text-tertiary mt-1">
            ${subscriptionBreakdown.revenue.projected.toFixed(2)}/yr projected
          </p>
        </div>

        <div className="bg-theme-bg rounded-lg p-6 border border-theme-border">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-theme-text-secondary">Paid Conversion</p>
            <TrendingUp className="w-5 h-5 text-theme-primary" />
          </div>
          <p className="text-3xl font-bold text-theme-text">
            {subscriptionBreakdown.paidPercentage}%
          </p>
          <p className="text-xs text-theme-text-tertiary mt-1">
            {subscriptionBreakdown.pro +
              subscriptionBreakdown.premium +
              subscriptionBreakdown.elite}{' '}
            paid users
          </p>
        </div>
      </div>

      {/* KPI Cards Row 2: Engagement */}
      <div className="grid grid-cols-1 @3@7xl:grid-cols-2 @5@7xl:grid-cols-4 gap-4">
        <div className="bg-theme-bg rounded-lg p-6 border border-theme-border">
          <p className="text-sm text-theme-text-secondary mb-2">Daily Active Users</p>
          <p className="text-3xl font-bold text-theme-text">{engagementMetrics.activeUsers.dau}</p>
        </div>

        <div className="bg-theme-bg rounded-lg p-6 border border-theme-border">
          <p className="text-sm text-theme-text-secondary mb-2">Weekly Active Users</p>
          <p className="text-3xl font-bold text-theme-text">{engagementMetrics.activeUsers.wau}</p>
        </div>

        <div className="bg-theme-bg rounded-lg p-6 border border-theme-border">
          <p className="text-sm text-theme-text-secondary mb-2">Monthly Active Users</p>
          <p className="text-3xl font-bold text-theme-text">{engagementMetrics.activeUsers.mau}</p>
        </div>

        <div className="bg-theme-bg rounded-lg p-6 border border-theme-border">
          <p className="text-sm text-theme-text-secondary mb-2">Retention Rate</p>
          <p className="text-3xl font-bold text-theme-text">{engagementMetrics.retentionRate}%</p>
          <p className="text-xs text-theme-text-tertiary mt-1">30-day active rate</p>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 @5@7xl:grid-cols-2 gap-6">
        {/* Subscription Breakdown Pie Chart */}
        <div className="bg-theme-surface rounded-lg border border-theme-border p-6">
          <h3 className="text-lg font-semibold text-theme-text mb-4">Subscription Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={[
                  {
                    name: 'Free',
                    value: subscriptionBreakdown.free,
                    color: COLORS.free,
                  },
                  {
                    name: 'Pro',
                    value: subscriptionBreakdown.pro,
                    color: COLORS.pro,
                  },
                  {
                    name: 'Premium',
                    value: subscriptionBreakdown.premium,
                    color: COLORS.premium,
                  },
                  {
                    name: 'Elite',
                    value: subscriptionBreakdown.elite,
                    color: COLORS.elite,
                  },
                ]}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={100}
                label={(entry) => `${entry.name}: ${entry.value}`}
              >
                {[COLORS.free, COLORS.pro, COLORS.premium, COLORS.elite].map((color, index) => (
                  <Cell key={`cell-${index}`} fill={color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1b26',
                  border: '1px solid #2d2f3d',
                  borderRadius: '0.5rem',
                }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Token Usage by Tier */}
        <div className="bg-theme-surface rounded-lg border border-theme-border p-6">
          <h3 className="text-lg font-semibold text-theme-text mb-4">Token Usage by Tier</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={[
                {
                  tier: 'Free',
                  Haiku: tokenUsage.byTier.free.haiku,
                  Sonnet: tokenUsage.byTier.free.sonnet,
                  Opus: tokenUsage.byTier.free.opus,
                },
                {
                  tier: 'Pro',
                  Haiku: tokenUsage.byTier.pro.haiku,
                  Sonnet: tokenUsage.byTier.pro.sonnet,
                  Opus: tokenUsage.byTier.pro.opus,
                },
                {
                  tier: 'Premium',
                  Haiku: tokenUsage.byTier.premium.haiku,
                  Sonnet: tokenUsage.byTier.premium.sonnet,
                  Opus: tokenUsage.byTier.premium.opus,
                },
              ]}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#2d2f3d" />
              <XAxis dataKey="tier" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1b26',
                  border: '1px solid #2d2f3d',
                  borderRadius: '0.5rem',
                }}
              />
              <Legend />
              <Bar dataKey="Haiku" fill={COLORS.haiku} stackId="a" />
              <Bar dataKey="Sonnet" fill={COLORS.sonnet} stackId="a" />
              <Bar dataKey="Opus" fill={COLORS.opus} stackId="a" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* User Growth Chart */}
        <div className="bg-theme-surface rounded-lg border border-theme-border p-6 @5xl:col-span-2">
          <h3 className="text-lg font-semibold text-theme-text mb-4">User Growth (Last 30 Days)</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={userGrowth.last30Days}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2d2f3d" />
              <XAxis
                dataKey="date"
                stroke="#9ca3af"
                fontSize={11}
                tickFormatter={(value) =>
                  new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                }
              />
              <YAxis stroke="#9ca3af" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1b26',
                  border: '1px solid #2d2f3d',
                  borderRadius: '0.5rem',
                }}
                labelFormatter={(value) => new Date(value).toLocaleDateString()}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="signups"
                stroke={COLORS.signups}
                strokeWidth={2}
                name="Signups"
              />
              <Line
                type="monotone"
                dataKey="conversions"
                stroke={COLORS.conversions}
                strokeWidth={2}
                name="Conversions"
              />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-theme-text-secondary">Avg Signups/Day</p>
              <p className="text-xl font-semibold text-theme-text">
                {userGrowth.summary.averageSignupsPerDay}
              </p>
            </div>
            <div>
              <p className="text-theme-text-secondary">Avg Conversions/Day</p>
              <p className="text-xl font-semibold text-theme-text">
                {userGrowth.summary.averageConversionsPerDay}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Token Usage Stats */}
      <div className="bg-theme-surface rounded-lg border border-theme-border p-6">
        <h3 className="text-lg font-semibold text-theme-text mb-4">Token Usage Statistics</h3>
        <div className="grid grid-cols-1 @3@7xl:grid-cols-2 @5@7xl:grid-cols-5 gap-6">
          <div>
            <p className="text-sm text-theme-text-secondary mb-1">Total Generations</p>
            <p className="text-2xl font-bold text-theme-text">
              {tokenUsage.totalGenerated.toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-sm text-theme-text-secondary mb-1">Avg per Free User</p>
            <p className="text-2xl font-bold text-theme-text">{tokenUsage.averagePerUser.free}</p>
          </div>
          <div>
            <p className="text-sm text-theme-text-secondary mb-1">Avg per Pro User</p>
            <p className="text-2xl font-bold text-theme-text">{tokenUsage.averagePerUser.pro}</p>
          </div>
          <div>
            <p className="text-sm text-theme-text-secondary mb-1">Avg per Premium User</p>
            <p className="text-2xl font-bold text-theme-text">
              {tokenUsage.averagePerUser.premium}
            </p>
          </div>
          <div>
            <p className="text-sm text-theme-text-secondary mb-1">Estimated Cost</p>
            <p className="text-2xl font-bold text-theme-text">${tokenUsage.costEstimate.total}</p>
            <p className="text-xs text-theme-text-tertiary mt-1">
              ${tokenUsage.costEstimate.perUser}/user
            </p>
          </div>
        </div>
      </div>

      {/* Engagement Comparison */}
      <div className="bg-theme-surface rounded-lg border border-theme-border p-6">
        <h3 className="text-lg font-semibold text-theme-text mb-4">Engagement by Tier</h3>
        <div className="grid grid-cols-1 @3@7xl:grid-cols-2 gap-6">
          <div>
            <p className="text-sm text-theme-text-secondary mb-3">Average Picks per User</p>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-theme-text">Free Users</span>
                  <span className="text-sm font-semibold text-theme-text">
                    {engagementMetrics.averagePicksPerUser.free}
                  </span>
                </div>
                <div className="h-2 bg-theme-bg rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gray-500"
                    style={{
                      width: `${Math.min((engagementMetrics.averagePicksPerUser.free / Math.max(engagementMetrics.averagePicksPerUser.free, engagementMetrics.averagePicksPerUser.pro, engagementMetrics.averagePicksPerUser.premium)) * 100, 100)}%`,
                    }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-theme-text">Pro Users</span>
                  <span className="text-sm font-semibold text-theme-text">
                    {engagementMetrics.averagePicksPerUser.pro}
                  </span>
                </div>
                <div className="h-2 bg-theme-bg rounded-full overflow-hidden">
                  <div
                    className="h-full"
                    style={{
                      backgroundColor: COLORS.pro,
                      width: `${Math.min((engagementMetrics.averagePicksPerUser.pro / Math.max(engagementMetrics.averagePicksPerUser.free, engagementMetrics.averagePicksPerUser.pro, engagementMetrics.averagePicksPerUser.premium)) * 100, 100)}%`,
                    }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-theme-text">Premium Users</span>
                  <span className="text-sm font-semibold text-theme-text">
                    {engagementMetrics.averagePicksPerUser.premium}
                  </span>
                </div>
                <div className="h-2 bg-theme-bg rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-500"
                    style={{
                      width: `${Math.min((engagementMetrics.averagePicksPerUser.premium / Math.max(engagementMetrics.averagePicksPerUser.free, engagementMetrics.averagePicksPerUser.pro, engagementMetrics.averagePicksPerUser.premium)) * 100, 100)}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div>
            <p className="text-sm text-theme-text-secondary mb-3">Average Parlays per User</p>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-theme-text">Free Users</span>
                  <span className="text-sm font-semibold text-theme-text">
                    {engagementMetrics.averageParlaysPerUser.free}
                  </span>
                </div>
                <div className="h-2 bg-theme-bg rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gray-500"
                    style={{
                      width: `${Math.min((engagementMetrics.averageParlaysPerUser.free / Math.max(engagementMetrics.averageParlaysPerUser.free, engagementMetrics.averageParlaysPerUser.pro, engagementMetrics.averageParlaysPerUser.premium)) * 100, 100)}%`,
                    }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-theme-text">Pro Users</span>
                  <span className="text-sm font-semibold text-theme-text">
                    {engagementMetrics.averageParlaysPerUser.pro}
                  </span>
                </div>
                <div className="h-2 bg-theme-bg rounded-full overflow-hidden">
                  <div
                    className="h-full"
                    style={{
                      backgroundColor: COLORS.pro,
                      width: `${Math.min((engagementMetrics.averageParlaysPerUser.pro / Math.max(engagementMetrics.averageParlaysPerUser.free, engagementMetrics.averageParlaysPerUser.pro, engagementMetrics.averageParlaysPerUser.premium)) * 100, 100)}%`,
                    }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-theme-text">Premium Users</span>
                  <span className="text-sm font-semibold text-theme-text">
                    {engagementMetrics.averageParlaysPerUser.premium}
                  </span>
                </div>
                <div className="h-2 bg-theme-bg rounded-full overflow-hidden">
                  <div
                    className="h-full bg-amber-500"
                    style={{
                      width: `${Math.min((engagementMetrics.averageParlaysPerUser.premium / Math.max(engagementMetrics.averageParlaysPerUser.free, engagementMetrics.averageParlaysPerUser.pro, engagementMetrics.averageParlaysPerUser.premium)) * 100, 100)}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Conversions Table */}
      {conversionMetrics.recentConversions.length > 0 && (
        <div className="bg-theme-surface rounded-lg border border-theme-border p-6">
          <h3 className="text-lg font-semibold text-theme-text mb-4">
            Recent Conversions (Last 30 Days)
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="border-b border-theme-border">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-medium text-theme-text-secondary">
                    Email
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-theme-text-secondary">
                    Display Name
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-theme-text-secondary">
                    Converted At
                  </th>
                </tr>
              </thead>
              <tbody>
                {conversionMetrics.recentConversions.map((conversion) => (
                  <tr
                    key={conversion.userId}
                    className="border-b border-theme-border hover:bg-theme-bg transition-colors"
                  >
                    <td className="py-3 px-4 text-sm text-theme-text">{conversion.email}</td>
                    <td className="py-3 px-4 text-sm text-theme-text">
                      {conversion.displayName || '-'}
                    </td>
                    <td className="py-3 px-4 text-sm text-theme-text-secondary">
                      {new Date(conversion.convertedAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
